import {
  Box,
  Button,
  Container,
  Divider,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/main-page/PageHeader';
import SearchInput from '../../components/main-page/SearchInput';
import { ReactComponent as Pen } from '../../assets/images/main-page/pen-icon.svg';
import {
  StyledCustomerTableBodyRow,
  StyledCustomerTableHeaderRow,
  StyledTableAvatar,
  StyledTableContainer,
} from '../../styles/TableStyle';
import { StyledTableBodyRow } from '../../styles/TableStyle';
import ProjectsDropdown from '../../components/main-page/ProjectsDropdown';
import CustomerFormDialog from '../../components/dialog/CustomerFormDialog';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { createClient, fetchClients } from '../../api/clientApi';
import PaginationComponent from '../../components/pagination/PaginationComponent';
import CreateClient from './CreateClient';
import { useTranslation } from 'react-i18next';
import CreateProjectModal from '../Projects/CreateProjectModal';
import CustomAvatar from '../../components/main-page/CustomAvatar';
import { usePermission } from '../../hooks/usePermission';
import {
  StyledProjectsDropDown,
  StyledSelect,
} from '../../styles/CustomersStyle/CustomerManagementStyle';

function CustomerManagement() {
  const [searchValue, setSearchValue] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [openDialog, setOpenDialog] = useState({
    open: false,
    id: null,
    action: '',
  });
  const [selectedUuid, setSelectedUuid] = useState(null);
  const [selectedClientUuid, setSelectedClientUuid] = useState(null);
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1); // Current page state
  const [perPage] = useState(10); // Number of cars per page
  const { t } = useTranslation('client');
  const customerPermission = {
    create: usePermission('Client.add'),
    edit: usePermission('Client.edit'),
    remove: usePermission('Client.remove'),
    addProject: usePermission('Project.add'),
  };

  const tableTitles = [
    t('client.clients_page.table_titles.client_name'),
    t('client.clients_page.table_titles.h.p'),
    t('client.clients_page.table_titles.office_tel'),
    t('client.clients_page.table_titles.address'),
    t('client.clients_page.table_titles.projects'),
    t('client.clients_page.table_titles.contacts'),
  ];

  // Handle pagination change
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const editClient = (uuid = null) => {
    setOpenDialog({
      open: true,
      id: uuid,
      action: 'edit',
    });
  };

  const handleOnSearchChange = useCallback(() => {
    //If searchValue has more than 2 characters or is empty, trigger the search
    if (searchValue.length > 2) {
      setCurrentPage(1); // Reset to first page for new search
      // queryClient.invalidateQueries(['clientData', currentPage, searchValue]); // Refetch the employee data
      queryClient.invalidateQueries(['clientData', currentPage, searchValue]); // Refetch the employee data
    }
  }, [searchValue, currentPage, queryClient]); // Add dependencies here

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      handleOnSearchChange();
    }, 300); // Adjust debounce time as needed

    return () => clearTimeout(delayDebounce);
  }, [searchValue, handleOnSearchChange]); // Include 'handleOnSearchChange' here

  const {
    data: clients,
    error,
    status,
    refetch,
  } = useQuery(
    ['clientData', currentPage, searchValue],
    () =>
      fetchClients({
        page: currentPage,
        limit: perPage,
        _q: searchValue,
        with: 'company,projects',
      }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      onError: (err) => {
        console.log(err);
      },
    },
  );

  const handleDialogOpen = (action, id = null) => {
    setOpenDialog({
      open: true,
      id,
      action,
    });
  };

  const handleClose = (event, reason) => {
    if (reason && reason === 'backdropClick') return;
    setOpenDialog({
      open: false,
      id: null,
      action: '',
    });
  };

  return (
    <Container>
      <PageHeader
        title={t('client.clients_page.main_title')}
        buttonText={t('client.clients_page.add_button')}
        handelClick={() => setShowCreateModal(true)}
        hideAddButton={!customerPermission.create}
      />

      <Box
        padding="24px 40px 97px"
        backgroundColor="rgba(232, 239, 242, 1)"
        sx={{ clipPath: 'polygon(100% 0, 100% 100%, 0 100%, 0 3%, 1.5% 0)' }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <SearchInput
            key="search_input_customer"
            placeholder={t('client.clients_page.search_placeholder')}
            setSearchValue={setSearchValue}
            paddingblock="12px"
          />
        </Stack>

        <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.3)' }} />

        <StyledTableContainer>
          {clients ? (
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <StyledCustomerTableHeaderRow lastchildwidth="205px">
                  {tableTitles.map((title) => {
                    return (
                      <TableCell key={title} align="center">
                        {title}
                      </TableCell>
                    );
                  })}
                </StyledCustomerTableHeaderRow>
              </TableHead>

              <TableBody>
                {status === 'loading' ? (
                  <TableRow>
                    <TableCell colSpan={tableTitles.length} align="center">
                      טוען נתונים...
                    </TableCell>
                  </TableRow>
                ) : clients.data.length > 0 ? (
                  clients.data.map((client, index) => {
                    return (
                      <React.Fragment key={`frag_${client.uuid}`}>
                        <StyledCustomerTableBodyRow key={`stbr_${client.uuid}`}>
                          <TableCell align="center">{client?.name}</TableCell>
                          <TableCell align="center">{client?.hp}</TableCell>
                          <TableCell align="center">{client?.phone}</TableCell>
                          <TableCell align="center">{client?.adress}</TableCell>
                          <TableCell align="center">
                            <StyledProjectsDropDown>
                              <StyledSelect value={0}>
                                <MenuItem value={0}>פרויקטים</MenuItem>
                                {client?.projects.map((project) => {
                                  return (
                                    <MenuItem
                                      key={project.uuid}
                                      value={project.uuid}
                                    >
                                      {project.name}
                                    </MenuItem>
                                  );
                                })}
                              </StyledSelect>
                            </StyledProjectsDropDown>
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              position: 'relative',
                            }}
                          >
                            <CustomAvatar
                              first_name={client.name.split(' ')[0]}
                              last_name={client.name.split(' ')[1]}
                              email={client?.email ? client?.email : 'Email'}
                              phone={client?.phone}
                            />
                          </TableCell>
                          <TableCell>
                            {customerPermission.addProject && (
                              <Button
                                variant="borderBlue"
                                onClick={() => {
                                  setSelectedClientUuid(client.uuid);
                                  setShowCreateProject(true);
                                }}
                                sx={{ padding: '7px 15px' }}
                              >
                                {t('client.clients_page.add_project_button')}
                              </Button>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {customerPermission.edit && (
                              <Stack
                                direction="row"
                                alignItems="center"
                                key={`edit_${client.uuid}`}
                                onClick={() => {
                                  setShowCreateModal(true);
                                  setSelectedUuid(client.uuid);
                                }}
                                spacing={0.75}
                                sx={{ cursor: 'pointer' }}
                              >
                                <Pen />
                                <Typography variant="h3" fontSize="16px">
                                  {t('client.clients_page.edit')}
                                </Typography>
                              </Stack>
                            )}
                          </TableCell>
                        </StyledCustomerTableBodyRow>
                        <TableRow
                          key={`tr_${client.uuid}`}
                          sx={{ height: '16px' }}
                        ></TableRow>
                      </React.Fragment>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell
                      key="no_data_found"
                      colSpan={tableTitles.length}
                      align="center"
                    >
                      {t('client.clients_page.no_clients_message')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          ) : (
            <Typography component="h2" variant="h2">
              {t('client.clients_page.no_clients_message')}
            </Typography>
          )}
        </StyledTableContainer>
      </Box>

      <CreateClient
        showCreateModal={showCreateModal}
        setShowCreateModal={setShowCreateModal}
        selectedUuid={selectedUuid}
        setSelectedUuid={setSelectedUuid}
        reloadData={refetch}
      />

      <CreateProjectModal
        showCreateModal={showCreateProject}
        setShowCreateModal={(val) => {
          setShowCreateProject(val);
          if (!val) {
            setSelectedClientUuid(null);
          }
        }}
        clientUuid={selectedClientUuid}
        // createProjectMutation={createProjectMutation}
      />

      {/*{openDialog.open && (
        <CustomerFormDialog
          key="formDialogue1"
          dialogData={openDialog}
          handleClose={handleClose}
        />
      )} */}
      {clients?.total > 0 && (
        <PaginationComponent
          key="pagination"
          totalItems={clients?.total || 0} // Total items from your fetched data
          perPage={perPage}
          currentPage={currentPage}
          handlePageChange={handlePageChange}
        />
      )}
    </Container>
  );
}

export default CustomerManagement;
